import { useState, useCallback, useEffect } from 'react';
import type { ExpandState } from '../types/sessionTimeline.types';

interface UseEventCardExpandOptions {
  defaultExpandedState?: ExpandState;
  collapsible?: boolean;
  onExpandStateChange?: (sessionId: string, state: ExpandState) => void;
  sessionId: string;
}

/**
 * EventCard의 확장/축소 상태를 관리하는 훅
 */
export function useEventCardExpand({
  defaultExpandedState = 'content',
  collapsible = true,
  onExpandStateChange,
  sessionId,
}: UseEventCardExpandOptions) {
  const [expandState, setExpandState] = useState<ExpandState>(defaultExpandedState);
  const [isInitialized, setIsInitialized] = useState(false);

  // defaultExpandedState가 변경되면 상태 업데이트 (초기화 시에만)
  useEffect(() => {
    if (!isInitialized) {
      setExpandState(defaultExpandedState);
      setIsInitialized(true);
    }
  }, [defaultExpandedState, isInitialized]);

  const expandToContent = useCallback(() => {
    if (!collapsible) return;
    setExpandState('content');
    onExpandStateChange?.(sessionId, 'content');
  }, [collapsible, onExpandStateChange, sessionId]);

  const expandToTimeline = useCallback(() => {
    if (!collapsible) return;
    setExpandState('timeline');
    onExpandStateChange?.(sessionId, 'timeline');
  }, [collapsible, onExpandStateChange, sessionId]);

  const collapse = useCallback(() => {
    if (!collapsible) return;
    setExpandState('collapsed');
    onExpandStateChange?.(sessionId, 'collapsed');
  }, [collapsible, onExpandStateChange, sessionId]);

  const toggleExpand = useCallback(() => {
    if (!collapsible) return;

    setExpandState((prevState) => {
      let newState: ExpandState;
      switch (prevState) {
        case 'collapsed':
          newState = 'content';
          break;
        case 'content':
          newState = 'collapsed';
          break;
        case 'timeline':
          newState = 'collapsed';
          break;
        default:
          newState = prevState;
      }
      onExpandStateChange?.(sessionId, newState);
      return newState;
    });
  }, [collapsible, onExpandStateChange, sessionId]);

  const toggleTimeline = useCallback(() => {
    if (!collapsible) return;

    setExpandState((prevState) => {
      let newState: ExpandState;
      switch (prevState) {
        case 'content':
          newState = 'timeline';
          break;
        case 'timeline':
          newState = 'content';
          break;
        case 'collapsed':
          // collapsed 상태에서는 타임라인 토글 불가
          return prevState;
        default:
          return prevState;
      }
      onExpandStateChange?.(sessionId, newState);
      return newState;
    });
  }, [collapsible, onExpandStateChange, sessionId]);

  return {
    expandState,
    expandToContent,
    expandToTimeline,
    collapse,
    toggleExpand,
    toggleTimeline,
    isCollapsed: expandState === 'collapsed',
    isContentExpanded: expandState === 'content',
    isTimelineExpanded: expandState === 'timeline',
  };
}
